import { useState } from "react";
import { useAuth, api } from "../App";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const CreatePost = ({ onPostCreated }) => {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);

  const getInitials = () => {
    if (user?.first_name) {
      return `${user.first_name[0]}${(user.last_name || 'L')[0]}`.toUpperCase();
    }
    if (user?.username) {
      return user.username.slice(0, 2).toUpperCase();
    }
    if (user?.phone) {
      return user.phone.slice(-2);
    }
    return "??";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim()) {
      toast.error("Please write something to post");
      return;
    }

    setPosting(true);
    try {
      const response = await api.post('/posts', {
        content: content.trim(),
        media_urls: [],
        media_types: []
      });
      
      toast.success("Post created!");
      setContent("");
      
      if (onPostCreated) {
        onPostCreated(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Card data-testid="create-post-card">
      <CardContent className="pt-4">
        <form onSubmit={handleSubmit}>
          <div className="flex gap-3">
            <Avatar className="w-10 h-10 shrink-0">
              <AvatarImage src={user?.avatar} alt={user?.username} />
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 space-y-3">
              <Textarea
                placeholder="What's on your mind?"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="resize-none border-0 p-0 text-base focus-visible:ring-0 min-h-[80px]"
                rows={3}
                data-testid="post-content-input"
              />

              {/* Actions */}
              <div className="flex items-center justify-end pt-2 border-t border-border">
                <Button 
                  type="submit"
                  disabled={posting || !content.trim()}
                  className="btn-hover gap-2"
                  data-testid="submit-post-btn"
                >
                  {posting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Post
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default CreatePost;
